import { chartInstance } from './state.js';
import type { CirclePackNode } from '../shared/types.js';
import { getColorDescriptions, saveColorDescription } from './api.js';
import { highlightContentNodes } from './contentFilter.js';
import { showContentNodesActive, showMyRolesActive, setShowMyRolesActive, setSkipNextPanelUpdate } from './state.js';
import { toggleTheme, getCurrentTheme } from './theme.js';
import { showErrorModal } from './utils/modalUtil.js';
import { generateMyRolesHtml, highlightClaimedRoles, attachMyRolesItemHandlers } from './legend/myRolesPanel.js';
import { t } from './utils/i18n.js';

// Cache for claimed role IDs
let cachedClaimedRoleIds: number[] | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5000; // 5 seconds

/**
 * Invalidate the claimed roles cache (call after claim/unclaim)
 */
function invalidateClaimedRolesCache(): void {
    cachedClaimedRoleIds = null;
    cacheTimestamp = 0;
}

async function getClaimedRoleIds(): Promise<number[]> {
    const now = Date.now();
    if (cachedClaimedRoleIds && (now - cacheTimestamp) < CACHE_DURATION) {
        return cachedClaimedRoleIds;
    }
    
    try {
        const response = await fetch('/api/user/claimed-role-ids', { credentials: 'include' });
        if (!response.ok) {
            console.warn('Failed to load claimed roles');
            return [];
        }
        const data = await response.json();
        cachedClaimedRoleIds = data.claimedRoleIds || [];
        cacheTimestamp = now;
        return cachedClaimedRoleIds;
    } catch (error) {
        // Silently fail - claimed roles are optional
        return [];
    }
}

interface ColorMapping {
    color: string;
    description: string;
    roles: string[];
}

function collectCustomColors(node: CirclePackNode, colorMap: Map<string, Set<string>>): void {
    // Skip white and undefined colors
    if (node.color && node.color !== '#ffffff' && node.color !== '#fff' && node.color !== 'white' && node.color !== 'rgba(107, 121, 250, 0.3)') {
        if (!colorMap.has(node.color)) {
            colorMap.set(node.color, new Set());
        }
        colorMap.get(node.color)!.add(node.name);
    }

    // Recursively process children
    if (node.children && node.children.length > 0) {
        node.children.forEach(child => collectCustomColors(child, colorMap));
    }
}

export function buildColorLegend(rootNode: CirclePackNode): ColorMapping[] {
    const colorMap = new Map<string, Set<string>>();
    collectCustomColors(rootNode, colorMap);

    // Convert to array and sort by color for consistency
    const legend: ColorMapping[] = Array.from(colorMap.entries())
        .map(([color, rolesSet]) => ({
            color,
            description: '',
            roles: Array.from(rolesSet).sort()
        }))
        .sort((a, b) => a.color.localeCompare(b.color));

    return legend;
}

export async function buildColorLegendWithDescriptions(rootNode: CirclePackNode): Promise<ColorMapping[]> {
    const legend = buildColorLegend(rootNode);
    const descriptions = await getColorDescriptions();
    
    // Apply descriptions from the table
    legend.forEach(item => {
        if (descriptions[item.color]) {
            item.description = descriptions[item.color];
        }
    });
    
    return legend;
}

export function renderLegendContent(legend: ColorMapping[], editMode: boolean = false): HTMLElement {
    const legendContent = document.createElement('div');
    legendContent.className = 'legend-button__content';

    if (legend.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'legend-button__empty';
        empty.textContent = t('legend.noCustomColors');
        legendContent.appendChild(empty);
        return legendContent;
    }

    const legendList = document.createElement('div');
    legendList.className = 'legend-button__list';

    legend.forEach(mapping => {
        const item = document.createElement('div');
        item.className = 'legend-button__item';

        const colorSwatch = document.createElement('div');
        colorSwatch.className = 'legend-button__swatch';
        colorSwatch.style.backgroundColor = mapping.color;
        colorSwatch.title = mapping.color;
        colorSwatch.setAttribute('aria-label', `Color: ${mapping.color}`);

        if (editMode) {
            // In edit mode, create editable input
            const colorLabel = document.createElement('input');
            colorLabel.type = 'text';
            colorLabel.className = 'legend-button__description-input';
            colorLabel.placeholder = 'No description';
            colorLabel.value = mapping.description || '';
            colorLabel.dataset.color = mapping.color;
            
            item.appendChild(colorSwatch);
            item.appendChild(colorLabel);
        } else {
            // In view mode, show description or role names
            const colorLabel = document.createElement('div');
            colorLabel.className = 'legend-button__label';
            colorLabel.textContent = mapping.description || mapping.roles.join(', ');
            colorLabel.title = mapping.roles.join(', ');
            
            item.appendChild(colorSwatch);
            item.appendChild(colorLabel);
        }

        legendList.appendChild(item);
    });

    legendContent.appendChild(legendList);
    return legendContent;
}

// New function: Create legend as a text button at bottom right
export async function attachLegendButton(chartWrapper: HTMLElement, rootNode: CirclePackNode): Promise<void> {
    let legend = await buildColorLegendWithDescriptions(rootNode);
    
    // Create the legend button (text button)
    const legendButton = document.createElement('button');
    legendButton.className = 'legend-button__toggle';
    legendButton.title = 'View color legend';
    legendButton.textContent = t('legend.toggleButton');
    legendButton.setAttribute('aria-label', 'Toggle color legend');

    // Create the legend popup
    const legendPopup = document.createElement('div');
    legendPopup.className = 'legend-button__popup';
    
    let isEditMode = false;
    
    const updateLegendDisplay = () => {
        const legendContent = renderLegendContent(legend, isEditMode);
        legendPopup.innerHTML = '';
        legendPopup.appendChild(legendContent);
        
        // Add content filter toggle
        const toggleSection = document.createElement('div');
        toggleSection.className = 'legend-button__toggle-section';

        const filterToggle = document.createElement('button');
        filterToggle.className = 'legend-button__filter-toggle' + (showContentNodesActive ? ' active' : '');
        
        const toggleIcon = document.createElement('span');
        toggleIcon.className = 'toggle-icon';
        toggleIcon.textContent = showContentNodesActive ? '✓' : '';
        
        const toggleLabel = document.createElement('span');
        toggleLabel.className = 'toggle-label';
        toggleLabel.textContent = t('legend.showRolesWithData');
        
        filterToggle.appendChild(toggleIcon);
        filterToggle.appendChild(toggleLabel);
        
        filterToggle.addEventListener('click', (e) => {
            e.stopPropagation();
            const newState = !showContentNodesActive;
            highlightContentNodes(newState);
            filterToggle.className = 'legend-button__filter-toggle' + (newState ? ' active' : '');
            const icon = filterToggle.querySelector('.toggle-icon');
            if (icon) icon.textContent = newState ? '✓' : '';
        });

        toggleSection.appendChild(filterToggle);
        legendPopup.appendChild(toggleSection);
        
        // Add edit/save button and section
        const buttonSection = document.createElement('div');
        buttonSection.className = 'legend-button__actions';
        
        if (isEditMode) {
            const saveBtn = document.createElement('button');
            saveBtn.className = 'legend-button__save-btn';
            saveBtn.textContent = 'Save';
            saveBtn.addEventListener('click', async (e) => {
                e.stopPropagation();
                saveBtn.disabled = true;
                saveBtn.textContent = 'Saving...';
                
                const inputs = legendPopup.querySelectorAll('.legend-button__description-input') as NodeListOf<HTMLInputElement>;
                let allSaved = true;
                
                for (const input of inputs) {
                    const color = input.dataset.color;
                    const description = input.value.trim();
                    const success = await saveColorDescription(color, description);
                    if (!success) {
                        allSaved = false;
                    }
                }
                
                if (allSaved) {
                    // Refresh legend data from database with new descriptions
                    legend = await buildColorLegendWithDescriptions(rootNode);
                    isEditMode = false;
                    updateLegendDisplay();
                } else {
                    saveBtn.disabled = false;
                    saveBtn.textContent = 'Save';
                    await showErrorModal('Save Error', 'Error saving one or more descriptions');
                }
            });
            
            const cancelBtn = document.createElement('button');
            cancelBtn.className = 'legend-button__cancel-btn';
            cancelBtn.textContent = 'Cancel';
            cancelBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                isEditMode = false;
                updateLegendDisplay();
            });
            
            buttonSection.appendChild(saveBtn);
            buttonSection.appendChild(cancelBtn);
        } else {
            const editBtn = document.createElement('button');
            editBtn.className = 'legend-button__edit-btn';
            editBtn.textContent = 'Edit';
            editBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                isEditMode = true;
                updateLegendDisplay();
            });
            
            buttonSection.appendChild(editBtn);
        }
        
        legendPopup.appendChild(buttonSection);
        
        // Add legal links section
        const legalSection = document.createElement('div');
        legalSection.className = 'legend-button__legal';
        
        const impressumLink = document.createElement('a');
        impressumLink.href = '/impressum.html';
        impressumLink.target = '_blank';
        impressumLink.textContent = t('legend.impressum');
        
        const separator = document.createElement('span');
        separator.textContent = ' | ';
        
        const datenschutzLink = document.createElement('a');
        datenschutzLink.href = '/datenschutz.html';
        datenschutzLink.target = '_blank';
        datenschutzLink.textContent = t('legend.privacy');
        
        legalSection.appendChild(impressumLink);
        legalSection.appendChild(separator);
        legalSection.appendChild(datenschutzLink);
        
        legendPopup.appendChild(legalSection);
    };

    // Toggle legend popup visibility
    let isOpen = false;
    legendButton.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!isOpen) {
            updateLegendDisplay();
            legendPopup.classList.add('visible');
            isOpen = true;
        } else {
            legendPopup.classList.remove('visible');
            isOpen = false;
        }
    });

    // Close popup when clicking outside
    document.addEventListener('click', (e) => {
        if (isOpen && e.target !== legendButton && !legendPopup.contains(e.target as Node)) {
            legendPopup.classList.remove('visible');
            isOpen = false;
        }
    });

    // Append button and popup directly to chartWrapper (no wrapper needed)
    chartWrapper.appendChild(legendButton);
    chartWrapper.appendChild(legendPopup);
}

export async function attachLegend(chartElement: HTMLElement, rootNode: CirclePackNode): Promise<void> {
    let legend = await buildColorLegendWithDescriptions(rootNode);
    
    // Create the legend button (question mark icon)
    const legendButton = document.createElement('button');
    legendButton.className = 'legend-button__toggle';
    legendButton.title = 'View color legend';
    legendButton.textContent = '?';
    legendButton.setAttribute('aria-label', 'Toggle color legend');

    // Create the legend popup
    const legendPopup = document.createElement('div');
    legendPopup.className = 'legend-button__popup';
    
    let isEditMode = false;
    
    const updateLegendDisplay = () => {
        const legendContent = renderLegendContent(legend, isEditMode);
        legendPopup.innerHTML = '';
        legendPopup.appendChild(legendContent);
        
        // Add content filter toggle and theme toggle
        const toggleSection = document.createElement('div');
        toggleSection.className = 'legend-toggle-section';

        const filterToggle = document.createElement('button');
        filterToggle.className = 'legend-button__filter-toggle' + (showContentNodesActive ? ' active' : '');
        
        const toggleIcon = document.createElement('span');
        toggleIcon.className = 'toggle-icon';
        toggleIcon.textContent = showContentNodesActive ? '✓' : '';
        
        const toggleLabel = document.createElement('span');
        toggleLabel.className = 'toggle-label';
        toggleLabel.textContent = t('legend.showRolesWithData');
        
        filterToggle.appendChild(toggleIcon);
        filterToggle.appendChild(toggleLabel);
        
        filterToggle.addEventListener('click', (e) => {
            e.stopPropagation();
            const newState = !showContentNodesActive;
            highlightContentNodes(newState);
            filterToggle.className = 'legend-button__filter-toggle' + (newState ? ' active' : '');
            const icon = filterToggle.querySelector('.toggle-icon');
            if (icon) icon.textContent = newState ? '✓' : '';
        });

        toggleSection.appendChild(filterToggle);
        
        legendPopup.appendChild(toggleSection);
        
        // Add edit/save button and section
        const buttonSection = document.createElement('div');
        buttonSection.className = 'legend-button__actions';
        
        if (isEditMode) {
            const saveBtn = document.createElement('button');
            saveBtn.className = 'legend-button__save-btn';
            saveBtn.textContent = 'Save';
            saveBtn.addEventListener('click', async (e) => {
                e.stopPropagation();
                saveBtn.disabled = true;
                saveBtn.textContent = 'Saving...';
                
                const inputs = legendPopup.querySelectorAll('.legend-button__description-input') as NodeListOf<HTMLInputElement>;
                let allSaved = true;
                
                for (const input of inputs) {
                    const color = input.dataset.color;
                    const description = input.value.trim();
                    const success = await saveColorDescription(color, description);
                    if (!success) {
                        allSaved = false;
                    }
                }
                
                if (allSaved) {
                    // Refresh legend data from database with new descriptions
                    legend = await buildColorLegendWithDescriptions(rootNode);
                    isEditMode = false;
                    updateLegendDisplay();
                } else {
                    saveBtn.disabled = false;
                    saveBtn.textContent = 'Save';
                    await showErrorModal('Save Error', 'Error saving one or more descriptions');
                }
            });
            
            const cancelBtn = document.createElement('button');
            cancelBtn.className = 'legend-button__cancel-btn';
            cancelBtn.textContent = 'Cancel';
            cancelBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                isEditMode = false;
                updateLegendDisplay();
            });
            
            buttonSection.appendChild(saveBtn);
            buttonSection.appendChild(cancelBtn);
        } else {
            const editBtn = document.createElement('button');
            editBtn.className = 'legend-button__edit-btn';
            editBtn.textContent = 'Edit';
            editBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                isEditMode = true;
                updateLegendDisplay();
            });
            
            buttonSection.appendChild(editBtn);
        }
        
        legendPopup.appendChild(buttonSection);
        
        // Add legal links section
        const legalSection = document.createElement('div');
        legalSection.className = 'legend-button__legal';
        
        const impressumLink = document.createElement('a');
        impressumLink.href = '/impressum.html';
        impressumLink.target = '_blank';
        impressumLink.textContent = t('legend.impressum');
        
        const separator = document.createElement('span');
        separator.textContent = ' | ';
        
        const datenschutzLink = document.createElement('a');
        datenschutzLink.href = '/datenschutz.html';
        datenschutzLink.target = '_blank';
        datenschutzLink.textContent = t('legend.privacy');
        
        legalSection.appendChild(impressumLink);
        legalSection.appendChild(separator);
        legalSection.appendChild(datenschutzLink);
        
        legendPopup.appendChild(legalSection);
    };
    
    // Initial display
    updateLegendDisplay();

    // Create a wrapper for button and popup
    const legendContainer = document.createElement('div');
    legendContainer.className = 'legend-button';
    legendContainer.appendChild(legendButton);
    legendContainer.appendChild(legendPopup);

    // Insert into chart container at the top right
    chartElement.appendChild(legendContainer);

    // Toggle visibility
    legendButton.addEventListener('click', (e) => {
        e.stopPropagation();
        legendPopup.classList.toggle('visible');
    });

    // Close popup when clicking outside
    document.addEventListener('click', (e) => {
        if (!legendContainer.contains(e.target as Node)) {
            legendPopup.classList.remove('visible');
        }
    });
}

// Show and highlight user's claimed roles
export async function showMyRolesOverview(): Promise<void> {
    try {
        // Set the flag so panel knows we're in my roles mode
        setShowMyRolesActive(true);
        
        // Show loading in panel
        const panelContent = document.querySelector('.role-panel__content');
        if (panelContent) {
            panelContent.innerHTML = '<div class="role-panel__loading-overlay"><div class="role-panel__loading-spinner"></div>Loading your roles...</div>';
        }
        
        // Load claimed role IDs (cached)
        const claimedRoleIds = await getClaimedRoleIds();
        
        // Highlight claimed roles in chart
        highlightClaimedRoles(claimedRoleIds);
        
        // Fetch full role info for each claimed role
        const roles: Array<{ id: number, name: string }> = [];
        for (const roleId of claimedRoleIds) {
            try {
                const roleRes = await fetch(`/api/roles/${roleId}`, { credentials: 'include' });
                if (roleRes.ok) {
                    const role = await roleRes.json();
                    roles.push({ id: role.id, name: role.name });
                }
            } catch (error) {
                console.error(`Failed to fetch role ${roleId}:`, error);
            }
        }
        
        // Show overview in panel
        if (panelContent) {
            panelContent.innerHTML = generateMyRolesHtml(claimedRoleIds, roles);
            attachMyRolesItemHandlers(panelContent as HTMLElement);
        }
    } catch (error) {
        console.error('Error showing my roles overview:', error);
    }
}

export async function reapplyMyRolesHighlight(): Promise<void> {
    try {
        const claimedRoleIds = await getClaimedRoleIds();
        
        const svg = document.getElementById('chart')?.querySelector('svg');
        if (svg) {
            svg.querySelectorAll('circle').forEach(circle => {
                const circleData = (circle as any).__data__;
                if (circleData && circleData.data && claimedRoleIds.includes(circleData.data.id)) {
                    circle.classList.add('role-panel__highlighted-myrole');
                } else {
                    circle.classList.remove('role-panel__highlighted-myrole');
                }
            });
        }
    } catch (error) {
        console.error('Error reapplying my roles highlight:', error);
    }
}

export function clearMyRolesHighlight(): void {
    // Clear the flag
    setShowMyRolesActive(false);
    
    // Remove highlights from circles
    const svg = document.getElementById('chart')?.querySelector('svg');
    if (svg) {
        svg.querySelectorAll('circle.role-panel__highlighted-myrole').forEach(circle => {
            circle.classList.remove('role-panel__highlighted-myrole');
        });
    }
    
    // Clear panel overview if it's showing
    const panelContent = document.querySelector('.role-panel__content');
    if (panelContent && panelContent.querySelector('.role-panel__my-roles-overview')) {
        panelContent.innerHTML = '<p class="role-panel__message role-panel__message--placeholder">Click on a role to view details</p>';
    }
}

/**
 * Update My Roles overview if it's currently visible
 */
async function updateMyRolesOverview(): Promise<void> {
    const panelContent = document.querySelector('.role-panel__content');
    if (panelContent && panelContent.querySelector('.role-panel__my-roles-overview')) {
        // My Roles panel is visible, refresh it
        await showMyRolesOverview();
    }
}

// Expose for legend button
(window as any).showMyRolesOverview = showMyRolesOverview;
(window as any).clearMyRolesHighlight = clearMyRolesHighlight;
(window as any).reapplyMyRolesHighlight = reapplyMyRolesHighlight;
(window as any).updateMyRolesOverview = updateMyRolesOverview;
(window as any).invalidateClaimedRolesCache = invalidateClaimedRolesCache;
